import React, { useEffect } from 'react';
import Layout from '../layout/Layout';
import { usePageContent } from '../../context/ContentContext';

const BulletList = ({ items }) => (
    <ul className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-gray-700 space-y-3">
        {items.map((item, index) => (
            <li key={index} className="flex items-start">
                <span className="text-primary-red mr-2">•</span>
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const OverseasProduction = () => {
    const { t, g } = usePageContent('overseasProduction');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative h-[70vh] overflow-hidden">
                <img
                    src={t('hero.image')}
                    alt={t('hero.title')}
                    className="absolute inset-0 w-full h-full object-cover scale-125"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40 flex items-center justify-center">
                    <h1 className="font-poppins font-light text-h1 leading-[60pt] tracking-[-0.02em] text-white text-center px-4">
                        {t('hero.title')}
                    </h1>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] mb-12 text-primary-dark-red">
                            {t('intro.title')}
                        </h2>
                        <div className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em]">
                            {t('intro.paragraphs').map((paragraph, index) => (
                                <p key={index} className="mb-6 last:mb-0">{paragraph}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Overseas Production */}
            <section className="py-24 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('reasons.title')}
                    </h2>

                    <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                        {t('reasons.cards').map((card, index) => (
                            <div key={index} className="text-center">
                                <div className="flex items-center justify-center mx-auto mb-6 h-32">
                                    <img
                                        src={card.image}
                                        alt={card.title}
                                        className="h-full w-auto object-contain"
                                    />
                                </div>
                                <h3 className="font-poppins font-semibold text-h3 mb-4 text-primary-red">{card.title}</h3>
                                <p className="font-poppins font-normal text-sub text-gray-700">
                                    {card.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Partner Locations */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('partners.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                            {t('partners.locations').map((location, index) => (
                                <div key={index} className="bg-accent-powder-green p-6 rounded-lg text-center">
                                    <h3 className="font-poppins font-semibold text-h3 text-primary-red">{location}</h3>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Quality Assurance */}
            <section className="py-24 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('quality.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-center mb-12">
                            {t('quality.intro')}
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-white p-8 rounded-lg shadow-sm">
                                <h3 className="font-poppins font-semibold text-h3 mb-4 text-primary-red">{t('quality.qcTitle')}</h3>
                                <BulletList items={t('quality.qcItems')} />
                            </div>

                            <div className="bg-white p-8 rounded-lg shadow-sm">
                                <h3 className="font-poppins font-semibold text-h3 mb-4 text-primary-red">{t('quality.auditTitle')}</h3>
                                <BulletList items={t('quality.auditItems')} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How We Work */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-16 text-primary-dark-red">
                        {t('howWeWork.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto space-y-8">
                        {t('howWeWork.steps').map((step, index) => (
                            <div key={index} className="flex items-start">
                                <div className="flex-shrink-0 bg-primary-red text-white w-10 h-10 rounded-full flex items-center justify-center font-poppins font-light mr-6">
                                    {index + 1}
                                </div>
                                <div>
                                    <h3 className="font-poppins font-semibold text-h3 mb-2 text-primary-red">{step.title}</h3>
                                    <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] text-gray-700">
                                        {step.body}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-primary-dark-red text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] mb-6">
                        {t('cta.title')}
                    </h2>
                    <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] max-w-2xl mx-auto mb-10">
                        {t('cta.body')}
                    </p>
                    <a
                        href={`mailto:${g('contact.email')}`}
                        className="inline-block px-8 py-4 bg-primary-red text-white hover:bg-pink-600 transition-colors duration-300 font-poppins font-semibold text-h3 rounded-lg"
                    >
                        {t('cta.buttonText')}
                    </a>
                </div>
            </section>
        </Layout>
    );
};

export default OverseasProduction;
